import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";

const TYPES = ["objection", "customer_feedback", "purchase_reason", "lost_reason", "sales_note"];
const TYPE_COLOR = {
  objection: "text-rose-400 border-rose-500/40",
  customer_feedback: "text-sky-400 border-sky-500/40",
  purchase_reason: "text-emerald-400 border-emerald-500/40",
  lost_reason: "text-amber-400 border-amber-500/40",
  sales_note: "text-slate-400 border-slate-600",
};

export default function Knowledge() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ entry_type: "sales_note", content: "", tags: "" });

  const load = () => api.get("/knowledge").then((r) => setItems(r.data));
  useEffect(() => { load().catch(() => setItems([])); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) { toast.error("Content required"); return; }
    try {
      await api.post("/knowledge", {
        entry_type: form.entry_type,
        content: form.content,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Saved");
      setForm({ ...form, content: "", tags: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="knowledge-base">
      <div className="tick-label text-sky-400 mb-2">/ Internal Knowledge Base</div>
      <h1 className="font-display font-black text-3xl text-slate-100 mb-2">Institutional Memory</h1>
      <p className="text-sm text-slate-400 mb-6 max-w-2xl">
        Capture objections, customer feedback, purchase reasons, lost-deal causes, and sales observations.
        Used over time to improve scoring, outreach, and conversion.
      </p>

      <form onSubmit={submit} className="panel p-5 mb-6 space-y-3 max-w-3xl">
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="tick-label block mb-1.5">Entry Type</label>
            <select data-testid="kn-type" value={form.entry_type} onChange={(e) => setForm({ ...form, entry_type: e.target.value })} className="w-full bg-[var(--bg-secondary)] border border-slate-700 text-slate-100 text-sm p-2.5 font-mono">
              {TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="tick-label block mb-1.5">Tags (comma-separated)</label>
            <input data-testid="kn-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="VA, telehealth, pricing" className="w-full bg-[var(--bg-secondary)] border border-slate-700 text-slate-100 text-sm p-2.5 font-mono" />
          </div>
        </div>
        <div>
          <label className="tick-label block mb-1.5">Content</label>
          <textarea data-testid="kn-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={3} placeholder="e.g. 'Customer said the $249 tier is fine if delivered in <48hrs' or 'Prospect declined: already uses Deltek capture tools'" className="w-full bg-[var(--bg-secondary)] border border-slate-700 text-slate-100 text-sm p-3 font-mono" />
        </div>
        <button type="submit" data-testid="kn-submit" className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[10px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">
          <Plus className="w-3.5 h-3.5" /> Save Entry
        </button>
      </form>

      {items === null ? (
        <Loader2 className="w-6 h-6 text-sky-400 animate-spin mx-auto" />
      ) : items.length === 0 ? (
        <div className="panel p-12 text-center text-slate-500 text-sm italic">No knowledge entries yet.</div>
      ) : (
        <div className="space-y-px bg-[var(--border-soft)] max-w-4xl">
          {items.map((k) => (
            <div key={k.knowledge_id} className="bg-[var(--bg-secondary)] p-4">
              <div className="flex items-start justify-between mb-1.5">
                <span className={`font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border ${TYPE_COLOR[k.entry_type] || TYPE_COLOR.sales_note}`}>
                  {k.entry_type.replace(/_/g, " ")}
                </span>
                <span className="font-mono text-[10px] text-slate-500">{(k.created_at || "").slice(0, 19).replace("T", " ")}</span>
              </div>
              <p className="text-sm text-slate-200 leading-relaxed">{k.content}</p>
              {(k.tags || []).length > 0 && (
                <div className="flex gap-1.5 mt-2">
                  {k.tags.map((t) => (
                    <span key={t} className="font-mono text-[9px] tracking-widest uppercase text-slate-500">#{t}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
